import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { ProfileSidebar, ProfileInformation, AccountSecurity, LimitedAccessBanner, ExpiredSubscriptionBanner } from '@/components/profile';
import { SubscriptionPlan } from '@/components/profile/SubscriptionPlan';
import { LearningHistory } from '@/components/profile/LearningHistory';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Loader2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { subscriptionService } from '@/services/subscriptionService';
import { userService } from '@/services/userService';
import type { User } from '@/types/user';

export const ProfilePage: React.FC = () => {
  const { user, isLoading, updateUser } = useUser();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasActiveSubscription, setHasActiveSubscription] = useState<boolean | null>(null);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(false);
  const [expiredSubscription, setExpiredSubscription] = useState<{ expireDate: string } | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle tab query parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && ['overview', 'subscription', 'history'].includes(tabParam)) {
      setActiveTab(tabParam);
      // Scroll to top when switching tabs
      window.scrollTo(0, 0);
      // Remove tab parameter from URL after setting the tab
      searchParams.delete('tab');
      navigate(`/profile${searchParams.toString() ? `?${searchParams.toString()}` : ''}`, { replace: true });
    }
  }, [searchParams, navigate]);

  // Check subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      if (!user) return;
      
      setIsCheckingSubscription(true);
      try {
        const response = await subscriptionService.getUserSubscriptions();
        const subscriptions = response.subscriptions || [];
        const hasActive = subscriptions.length > 0 && subscriptions.some(sub => sub.is_active);
        setHasActiveSubscription(hasActive);
        
        // Check if there are expired subscriptions (subscriptions exist but none are active)
        if (!hasActive && subscriptions.length > 0) {
          // Find the most recent expired subscription
          const expiredSubs = subscriptions.filter(sub => !sub.is_active);
          if (expiredSubs.length > 0) {
            // Sort by expire_date descending to get the most recent
            const mostRecent = expiredSubs.sort((a, b) => 
              new Date(b.expire_date).getTime() - new Date(a.expire_date).getTime()
            )[0];
            setExpiredSubscription({ expireDate: mostRecent.expire_date });
          } else {
            setExpiredSubscription(null);
          }
        } else {
          setExpiredSubscription(null);
        }
      } catch (error) {
        console.error('Failed to check subscription status:', error);
        // Default to false if check fails
        setHasActiveSubscription(false);
        setExpiredSubscription(null);
      } finally {
        setIsCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, [user, refreshKey]);

  // Handle payment success redirect
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');

    if (paymentStatus === 'success' && sessionId) {
      // Show success toast
      toast.success('Your subscription has been processed successfully.');

      // Switch to subscription tab
      setActiveTab('subscription');
      
      // Force refresh of SubscriptionPlan component
      setRefreshKey(prev => prev + 1);

      // Remove query parameters from URL
      searchParams.delete('payment');
      searchParams.delete('session_id');
      navigate(`/profile${searchParams.toString() ? `?${searchParams.toString()}` : ''}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB.');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      toast.error('No file selected. Please select an image first.');
      return;
    }

    setIsUploadingImage(true);
    try {
      const response = await userService.uploadProfileImage(file);
      
      // Update tokens
      userService.updateTokens(response);
      
      // Update user context
      if (!response.user) {
        throw new Error('User data not found in response');
      }
      const updatedUser: User = {
        ...user!,
        ...response.user,
        phone_code: response.user.phone_code || response.user.mobile_region_code,
        phone: response.user.phone || response.user.mobile_number,
        role: response.user.fk_role_id === 3 ? 'plus' : 'visitor',
        isAuthenticated: true,
        avatar: response.user.avatar,
        profile: {
          ...user!.profile,
          ...response.user,
          id: response.user.id || user!.profile?.id || user!.id,
          avatar: response.user.avatar,
        },
      };
      updateUser(updatedUser);

      toast.success('Profile image uploaded successfully!');

      setImagePreview(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Failed to upload profile image:', error);
      toast.error(error.response?.data?.message || 'Failed to upload profile image. Please try again.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveImagePreview = () => {
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Please log in to view your profile</div>
      </div>
    );
  }

  // Get avatar URL from user
  const avatarUrl = user.avatar || user.profile?.avatar || null;
  
  // Get user initials for fallback
  const getInitials = () => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) {
      return firstName[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="min-h-screen relative text-left overflow-x-hidden max-w-full">
      {/* Cosmic Background with Stars - Reduced for mobile */}
      <div className="floating-stars">
        <div className="star absolute top-10 left-10 w-2 h-2 hidden sm:block"></div>
        <div className="star absolute top-20 right-20 w-1 h-1"></div>
        <div className="star absolute top-40 left-1/4 w-1.5 h-1.5 hidden md:block"></div>
        <div className="star absolute top-60 right-1/3 w-1 h-1"></div>
        <div className="star absolute top-80 left-1/3 w-2 h-2 hidden sm:block"></div>
        <div className="star absolute top-96 right-1/4 w-1.5 h-1.5 hidden md:block"></div>
        <div className="star absolute top-32 left-3/4 w-1 h-1"></div>
        <div className="star absolute top-52 right-10 w-1.5 h-1.5 hidden sm:block"></div>
        <div className="star absolute top-72 left-10 w-1 h-1 hidden md:block"></div>
        <div className="star absolute top-88 right-1/2 w-2 h-2"></div>
        {/* Additional floating particles - Hidden on mobile for performance */}
        <div className="particle absolute top-1/4 left-1/6 w-3 h-3 hidden lg:block"></div>
        <div className="particle absolute top-1/3 right-1/5 w-2 h-2 hidden lg:block"></div>
        <div className="particle absolute bottom-1/4 left-1/5 w-4 h-4 hidden lg:block"></div>
        <div className="particle absolute bottom-1/3 right-1/6 w-2 h-2 hidden lg:block"></div>
      </div>

      {/* Mobile Breadcrumb Navigation */}
      <div className="relative z-10 pt-4 pb-2 px-4 text-left lg:hidden">
        <nav className="text-white text-sm">
          <span className="text-gray-300">Home</span>
          <span className="mx-2">/</span>
          <span className="text-white">My Profile</span>
        </nav>
      </div>

      {/* Desktop Breadcrumb Navigation */}
      <div className="relative z-10 pt-6 pb-6 px-6 text-left hidden lg:block">
        <nav className="text-white text-sm">
          <span className="text-gray-300">Home</span>
          <span className="mx-2">/</span>
          <span className="text-white">My Profile</span>
        </nav>
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 py-4 sm:py-8 text-left overflow-x-hidden max-w-full">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 max-w-full">
          {/* Desktop Sidebar */}
          <div className="hidden lg:block lg:w-80 flex-shrink-0 text-left">
            <ProfileSidebar 
              user={user} 
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0 text-left max-w-full overflow-x-hidden">
            {/* Mobile Profile Header - Profile Picture and Name */}
            <div className="lg:hidden mb-6 max-w-full">
              <div className="text-center">
                <div className="relative inline-block">
                  <div className="relative profile-glow">
                    <Avatar className="w-40 h-40 sm:w-48 sm:h-48 border-4 border-white overflow-hidden mx-auto">
                      <AvatarImage 
                        src={imagePreview || avatarUrl || undefined} 
                        alt={`${user.first_name} ${user.last_name}`}
                        className="object-cover"
                      />
                      <AvatarFallback className="bg-gradient-to-br from-purple-400 to-blue-500 text-white text-3xl sm:text-4xl font-bold">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                    {/* Camera icon */}
                    <label
                      htmlFor="avatar-upload-mobile"
                      className="absolute bottom-1 right-3 bg-gray-400 rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors p-1.5 sm:p-2 cursor-pointer"
                    >
                      <img src="/camera_1.svg" alt="Camera" className="w-[20px] h-[20px] sm:w-[24px] sm:h-[24px]"/>
                      <input
                        id="avatar-upload-mobile"
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
                <h2 className="text-white text-xl sm:text-2xl font-bold mt-4 sm:mt-5">
                  {user.first_name} {user.last_name || ''}
                </h2>
                
                {/* Image Upload Controls */}
                {imagePreview && (
                  <div className="flex flex-col items-center gap-2 mt-3">
                    <div className="flex gap-2">
                      <Button
                        onClick={handleImageUpload}
                        disabled={isUploadingImage}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                      >
                        {isUploadingImage ? (
                          <>
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          'Upload'
                        )}
                      </Button>
                      <Button
                        onClick={handleRemoveImagePreview}
                        disabled={isUploadingImage}
                        size="sm"
                        variant="outline"
                        className="text-white border-gray-600 hover:bg-gray-700 text-xs p-2"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400">Click camera icon to change image</p>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Tab Navigation */}
            <div className="lg:hidden mb-6 max-w-full">
              <div className="flex space-x-1 bg-white/10 backdrop-blur-sm rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'overview' 
                      ? 'bg-white text-black' 
                      : 'bg-black text-white hover:opacity-80'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('subscription')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'subscription' 
                      ? 'bg-white text-black' 
                      : 'bg-black text-white hover:opacity-80'
                  }`}
                >
                  Plan
                </button>
                <button
                  onClick={() => setActiveTab('history')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === 'history' 
                      ? 'bg-white text-black' 
                      : 'bg-black text-white hover:opacity-80'
                  }`}
                >
                  History
                </button>
              </div>
            </div>

            {/* Content Sections */}
            <div className="profile-main-content-mobile">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Show expired subscription banner if subscription expired */}
                  {hasActiveSubscription === false && !isCheckingSubscription && expiredSubscription && (
                    <ExpiredSubscriptionBanner 
                      expireDate={expiredSubscription.expireDate}
                      onReactivate={() => setActiveTab('subscription')}
                    />
                  )}
                  {/* Show limited access banner if user never had a subscription */}
                  {hasActiveSubscription === false && !isCheckingSubscription && !expiredSubscription && (
                    <LimitedAccessBanner 
                      onReactivate={() => setActiveTab('subscription')}
                    />
                  )}
                  <ProfileInformation user={user} />
                  <AccountSecurity user={user} />
                </div>
              )}
              {activeTab === 'subscription' && (
                <div className="space-y-6">
                  {/* Show expired subscription banner if subscription expired */}
                  {hasActiveSubscription === false && !isCheckingSubscription && expiredSubscription && (
                    <ExpiredSubscriptionBanner 
                      expireDate={expiredSubscription.expireDate}
                      onReactivate={() => setActiveTab('subscription')}
                    />
                  )}
                  {/* Show limited access banner if user never had a subscription */}
                  {hasActiveSubscription === false && !isCheckingSubscription && !expiredSubscription && (
                    <LimitedAccessBanner 
                      onReactivate={() => setActiveTab('subscription')}
                    />
                  )}
                  <SubscriptionPlan key={refreshKey} user={user} />
                </div>
              )}
              {activeTab === 'history' && (
                <LearningHistory user={user} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
